import { Dialog, Select, MenuItem } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect } from 'react';
import { useService } from '~/services/service.base';
import { ROUTES } from '~/routes/routes';
import { useNavigate } from 'react-router-dom';
import { DialogService } from '~/services/service.dialog';
import { DropdownArrow } from '~/views/atoms/svg-icons/dropdown-arrow';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import { SwiperNavigations } from '~/views/molecules/swiper.navigations';
import LinearProgress from '@mui/material/LinearProgress';
import { WrapperService } from '~/services/service.wrapper';
import { AlertDialog } from '~/views/molecules/alert.dialog';
import 'swiper/css';

export const RedeemDialog: FC = observer(() => {
    const dialogService = useService(DialogService);
    const wrapperService = useService(WrapperService);
    const { authService } = wrapperService;
    const { redeemDialogSrv, alertDialogSrv } = dialogService;

    useEffect(() => {
        window.addEventListener('keyup', e => {
            redeemDialogSrv.onRedeemKeyUp(e, alertDialogSrv);
        });
        alertDialogSrv.setData({
            headerText: 'Something went wrong',
            mainText: 'Sorry, we can’t complete your redemption at this time',
            showContactInfo: true,
        });
    }, []);

    const navigate = useNavigate();
    const goToPurchaseHistory = (event: any) => {
        event.preventDefault();
        redeemDialogSrv.handleClose();
        navigate(ROUTES.PURCHASE_HISTORY.path);
    };

    return (
        <React.Fragment>
            <Dialog open={redeemDialogSrv.open} className="hr-modal-dialog">
                <div id="redeem-modal" className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <button type="button" className="close" onClick={redeemDialogSrv.handleClose}>
                                <figure>
                                    <img src="/assets/img/modal-close-btn.svg" />
                                </figure>
                            </button>
                        </div>
                        <div className="modal-body">
                            {redeemDialogSrv.showStep1 && (
                                <div className="modalContent step1">
                                    {redeemDialogSrv.showLoading ? <LinearProgress /> : null}
                                    <h3>Redeem Free Play</h3>
                                    <div className="redeem-freeplay-wrap">
                                        <div className="redeem-freeplay-icon">
                                            <figure>
                                                <img src="/assets/img/free-play-redeem.svg" />
                                            </figure>
                                        </div>
                                        <div className="redeem-freeplay-text">
                                            Free Play Balance ${authService.currentUser?.freePlay}
                                        </div>
                                    </div>
                                    <p>
                                        Select The Amount You Want To Redeem
                                        <br /> From Your Available Free Play Credits.
                                    </p>
                                    <div className="redeem-amounts-wrap">
                                        <Swiper
                                            className="redeem-amounts-items redeem-slider"
                                            spaceBetween={10}
                                            slidesPerView={3}
                                            onSlideChange={redeemDialogSrv.handleSlideChange}
                                        >
                                            <SwiperNavigations />
                                            {redeemDialogSrv.redeemAmounts.map((item, index) => (
                                                <SwiperSlide
                                                    key={index}
                                                    className={
                                                        'redeem-amounts-item' +
                                                        (item.disabled ? ' disabled' : '') +
                                                        (item.active ? ' active' : '')
                                                    }
                                                    onClick={() => redeemDialogSrv.handleSlideClick(item)}
                                                >
                                                    <span>${item.value}</span>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                    </div>
                                    <div className="redeem-locations-wrap">
                                        <Select
                                            className="hr-select"
                                            IconComponent={DropdownArrow}
                                            value={redeemDialogSrv.selectedLocation}
                                            onChange={redeemDialogSrv.handleLocationChange}
                                        >
                                            <MenuItem className="redeem-locations-menu hide" value={'0'}>
                                                <span>View all participating locations</span>
                                            </MenuItem>
                                            {redeemDialogSrv.redeemLocations.map((redeemLocation, index) => (
                                                <MenuItem
                                                    key={index}
                                                    className="redeem-locations-menu"
                                                    value={redeemLocation.code}
                                                >
                                                    <figure>
                                                        <img src="/assets/img/redeem-dropdown-icon.svg" />
                                                    </figure>
                                                    <span>{redeemLocation.name}</span>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </div>
                                    <div className="redeem-btn-wrap">
                                        <button
                                            type="button"
                                            id="redeem-step1-btn"
                                            disabled={
                                                !redeemDialogSrv.selectedRedeem ||
                                                redeemDialogSrv.selectedLocation === '0'
                                            }
                                            className="btn"
                                            onClick={() => redeemDialogSrv.handleOpenStep2(alertDialogSrv)}
                                        >
                                            Redeem
                                        </button>
                                    </div>
                                    <div className="redeem-tooltip">
                                        <div>
                                            You can redeem your Free Play credits with a minimum of $50,
                                            <br /> Deposits will be completed within 24 hours.
                                        </div>
                                    </div>
                                </div>
                            )}
                            {redeemDialogSrv.showStep2 && (
                                <div className="modalContent step2">
                                    <h3>Congrats!</h3>
                                    <h3 id="redeem-success-amount" className="redeem-success-amount">
                                        ${redeemDialogSrv.selectedRedeem.value}
                                    </h3>
                                    <h5 id="redeem-success-location">{redeemDialogSrv.getSelectedLocation()}</h5>
                                    <p>Your Request For Free Play Was Successfully Submitted</p>
                                    <p className="redeem-success-text">You Can Check The Status In Your</p>
                                    <p className="redeem-success-link">
                                        <a href="#" onClick={e => goToPurchaseHistory(e)}>
                                            Purchase History
                                        </a>
                                        .
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>
            {alertDialogSrv.open ? <AlertDialog error={redeemDialogSrv.lastError} /> : null}
        </React.Fragment>
    );
});
